import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Row, Col, Table } from 'react-bootstrap'
import { OnityPolicyData } from '../../auth/core/_models'
import { useAuth } from '../../auth'
import { KTSVG } from '../../../../_metronic/helpers'

// IST Date helpers
const formatDateIST = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    const dateOnly = dateString.split('T')[0]
    const parts = dateOnly.split('-')
    if (parts.length >= 3) {
      const year = parts[0]
      const month = parts[1]
      const day = parts[2]
      return `${day}/${month}/${year}`
    }
    return dateString
  } catch {
    return ''
  }
}

const toISTInputFormat = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    const dateOnly = dateString.split('T')[0]
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      return dateOnly
    }
    const parts = dateOnly.split('-')
    if (parts.length >= 3) {
      return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`
    }
    return ''
  } catch {
    return ''
  }
}

type Props = {
  policyData: OnityPolicyData
  policyEffectiveDate?: string
  policyCrmDbName?: string
  onSuccess: () => void
  preloadedMembers?: EnrolledMember[]
  initialDependents?: any[]
}

interface EnrolledMember {
  Id: string
  EmployeeNumber: string
  Name: string
  Dob: string
  Relation: string
  Gender: string
  Premium: number
  Grade: string
  CrmClientId: string
  PremiumOpted: boolean | null
  AdditionalPremium: number | null
  LivesCharged: number | null
  GradeLower: string | null
  TopupName: string | null
  TopupSumInsured: number | null
  TopupBasePremium: number | null
  TopupGst: number | null
  TopupTotalPremium: number | null
  BasePremium: number | null
  BaseGst: number | null
  BaseTotalPremium: number | null
  TotalPremiumPayable: number | null
  TotalGst: number | null
  SumInsured: number | null
  BaseSumInsured: number | null
  CreatedAt: string
}

type Dependent = {
  id: number
  name: string
  dob: string
  relation: string
  gender: string
}

// Allowed relations: Parents and Parents-in-law
const RELATIONS = ['Parent', 'ParentInLaw']

const GENDERS = ['Male', 'Female', 'Other']

const GST_RATE = 0.18

// Sum Insured options for MCX parental policy
const SUM_INSURED_OPTIONS = [
  { label: '2 Lakhs', value: 200000 },
  { label: '4 Lakhs', value: 400000 },
  { label: '6 Lakhs', value: 600000 },
]

// Premium lookup: sumInsured -> { 1P, 2P, 3P, 4P } (Group Premium, Excluding GST@18%)
const PREMIUM_TABLE: Record<number, { p1: number; p2: number; p3: number; p4: number }> = {
  200000: { p1: 25909, p2: 41001, p3: 56848, p4: 75184 },
  400000: { p1: 40999, p2: 63638, p3: 87408, p4: 114912 },
  600000: { p1: 63639, p2: 97596, p3: 133251, p4: 174509 },
}

export const McxParentalEnrollmentForm: React.FC<Props> = ({
  policyData,
  policyEffectiveDate,
  policyCrmDbName,
  onSuccess,
  preloadedMembers,
  initialDependents,
}) => {
  const gstRate = GST_RATE

  const [dependents, setDependents] = useState<Dependent[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingDependentId, setEditingDependentId] = useState<number | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [selectedSumInsured, setSelectedSumInsured] = useState<number>(200000)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [enrolledMembers, setEnrolledMembers] = useState<EnrolledMember[]>([])
  const [loadingFrozen, setLoadingFrozen] = useState(false)
  const { currentUser } = useAuth()

  const [newDependent, setNewDependent] = useState<Dependent>({
    id: 0,
    name: '',
    dob: '',
    relation: '',
    gender: '',
  })

  // Max 2 Parents + Max 2 Parent-in-laws = Max 4 total
  const maxTotalDependents = 4
  const maxParents = 2
  const maxParentInLaws = 2

  useEffect(() => {
    if (preloadedMembers && preloadedMembers.length > 0) {
      setEnrolledMembers(preloadedMembers)
      setLoadingFrozen(false)
    } else if (initialDependents && initialDependents.length > 0) {
      const mappedDependents = initialDependents
        .map((item, index) => {
          let relation = item.Relation
          const rLower = relation ? relation.toLowerCase() : ''

          if (['father', 'mother', 'parent'].includes(rLower)) relation = 'Parent'
          else if (rLower.includes('law')) relation = 'ParentInLaw'

          return {
            id: item.sid || Date.now() + index,
            name: item.Name,
            dob: item.DOB ? toISTInputFormat(item.DOB) : '',
            relation: relation,
            gender: item.Gender || '',
          }
        })
        .filter(
          (dep) => dep.relation === 'Self' || dep.relation === 'Parent' || dep.relation === 'ParentInLaw'
        )
      setDependents(mappedDependents)
    } else {
      // Initialize with Employee as first dependent (saved without coverage)
      setDependents([
        {
          id: 1,
          name: policyData.EmployeeName,
          dob: policyData.DOB ? toISTInputFormat(policyData.DOB) : '',
          relation: 'Self',
          gender: policyData.Gender || '',
        },
      ])
    }
  }, [policyData, preloadedMembers, initialDependents])

  const getPremiumForCount = (sumInsured: number, count: number): number => {
    const entry = PREMIUM_TABLE[sumInsured]
    if (!entry || count <= 0) return 0
    if (count === 1) return entry.p1
    if (count === 2) return entry.p2
    if (count === 3) return entry.p3
    return entry.p4 // 4 or more
  }

  const getPremiumLabel = (count: number): string => {
    if (count === 1) return '1 Parent'
    if (count === 2) return '2 Parents'
    if (count === 3) return '3 Parents'
    return '4 Parents'
  }

  const calculatePremium = () => {
    const familyDependents = dependents.filter((d) => d.relation !== 'Self')
    const dependentCount = familyDependents.length

    const dependentPremium = getPremiumForCount(selectedSumInsured, dependentCount)

    const dependentGST = dependentPremium * gstRate
    const dependentTotal = dependentPremium + dependentGST

    const totalTaxable = dependentPremium
    const totalGST = dependentGST
    const totalPayable = totalTaxable + totalGST

    return {
      dependentPremium,
      dependentCount,
      dependentGST,
      dependentTotal,
      gst: totalGST,
      total: totalPayable,
    }
  }

  const parentCount = dependents.filter((d) => d.relation !== 'Self').length
  const isMaxParentsReached = parentCount >= maxTotalDependents

  const handleAddClick = () => {
    setError(null)

    if (isMaxParentsReached) {
      return
    }

    setNewDependent({
      id: Date.now(),
      name: '',
      dob: '',
      relation: '',
      gender: '',
    })
    setShowAddModal(true)
  }

  const validateDependent = (dep: Dependent) => {
    setError(null)
    if (!dep.name || !dep.dob || !dep.relation || !dep.gender) {
      setError('All fields are required')
      return false
    }

    if (!RELATIONS.includes(dep.relation)) {
      setError('Only Parent or Parent-in-law relations are allowed.')
      return false
    }

    // Exclude the currently-editing dependent from counts
    const otherDeps = editingDependentId
      ? dependents.filter((d) => d.id !== editingDependentId)
      : dependents

    // Per-category validation: max 2 Parents and max 2 Parent-in-laws
    const existingParentCount = otherDeps.filter((d) => d.relation === 'Parent').length
    const existingParentInLawCount = otherDeps.filter((d) => d.relation === 'ParentInLaw').length

    if (dep.relation === 'Parent' && existingParentCount >= maxParents) {
      setError('Maximum 2 Parents allowed (Father & Mother).')
      return false
    }

    if (dep.relation === 'ParentInLaw' && existingParentInLawCount >= maxParentInLaws) {
      setError('Maximum 2 Parent-in-laws allowed (Father-in-law & Mother-in-law).')
      return false
    }

    const totalCount = existingParentCount + existingParentInLawCount
    if (totalCount >= maxTotalDependents) {
      setError('Maximum 4 members allowed (2 Parents + 2 Parent-in-laws).')
      return false
    }

    return true
  }

  const saveDependent = () => {
    if (!validateDependent(newDependent)) return

    if (editingDependentId) {
      // Update existing dependent
      setDependents(dependents.map((d) => (d.id === editingDependentId ? newDependent : d)))
    } else {
      // Add new dependent
      setDependents([...dependents, newDependent])
    }
    setEditingDependentId(null)
    setShowAddModal(false)
  }

  const handleEditClick = (dep: Dependent) => {
    setError(null)
    setEditingDependentId(dep.id)
    setNewDependent({ ...dep })
    setShowAddModal(true)
  }

  const removeDependent = (id: number) => {
    const updated = dependents.filter((d) => d.id !== id)
    setDependents(updated)
    setError(null)
  }

  const handleSubmit = async () => {
    if (isSubmitting) return

    setError(null)
    setIsSubmitting(true)

    const premiumData = calculatePremium()

    // Employee is saved without coverage, only parents share sum insured
    const familyMembersWithPremium = dependents.map((dep) => {
      const baseMember = {
        EmployeeNumber: currentUser?.EmployeeId,
        name: dep.name,
        dob: dep.dob,
        relation: dep.relation,
        gender: dep.gender,
        premium: dep.relation === 'Self' ? 0 : premiumData.dependentPremium,
        Grade: policyData.Grade,
        crm_client_id: policyCrmDbName || policyData.CorpCode,
        Email: currentUser?.Email,
        CorporateCode: policyData.CorporateCode,
        SumInsured: selectedSumInsured,
      }

      if (dep.relation === 'Self') {
        return {
          ...baseMember,
          premiumOpted: false,
          additionalPremium: premiumData.total,
          livesCharged: premiumData.dependentCount,
          grade: policyData.Grade,

          // No flexi/topup for this policy
          TopupName: '',
          topupsumInsured: 0,
          topup_base_premium: 0,
          topup_gst: 0,
          topup_total_premium: 0,

          // Base (Dependent/Parent) details
          base_premium: premiumData.dependentPremium,
          base_gst: premiumData.dependentGST,
          base_total_premium: premiumData.dependentTotal,

          // Grand totals
          total_premium_payable: premiumData.total,
          total_gst: premiumData.gst,
        }
      }

      return baseMember
    })

    console.log('Submitting payload:', familyMembersWithPremium)

    try {
      const response = await fetch(
        'https://etl.futurisk.in/webhook/2dd87d62-8fd3-4cf6-a7e9-2ae88a23af4c',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(familyMembersWithPremium),
        }
      )

      if (response.ok) {
        setShowSuccess(true)
      } else {
        setError('Failed to submit enrollment. Please try again.')
        setIsSubmitting(false)
      }
    } catch (e) {
      console.error(e)
      setError('Network error occurred. Please try again.')
      setIsSubmitting(false)
    }
  }

  const premiumInfo = calculatePremium()

  // ─── Frozen / Read-only State ───────────────────────────────────────
  if (policyData.freezeEnrolment) {
    if (loadingFrozen && (!preloadedMembers || preloadedMembers.length === 0))
      return (
        <div className='text-center p-5 text-primary'>
          <span className='spinner-border spinner-border-sm align-middle ms-2'></span> Loading
          enrollment details...
        </div>
      )

    const membersToDisplay =
      preloadedMembers && preloadedMembers.length > 0 ? preloadedMembers : enrolledMembers

    const frozenPremium = {
      dependentPremium: membersToDisplay.reduce((acc, m) => acc + (m.BasePremium || 0), 0),
      gst: membersToDisplay.reduce((acc, m) => acc + (m.TotalGst || 0), 0),
      total: membersToDisplay.reduce((acc, m) => acc + (m.TotalPremiumPayable || 0), 0),
    }

    // Get the actual enrolled sum insured from member data
    const enrolledMember = membersToDisplay.find((m) => (m.BaseSumInsured && m.BaseSumInsured > 0) || (m.SumInsured && m.SumInsured > 0))
    const enrolledSumInsured = enrolledMember?.BaseSumInsured || enrolledMember?.SumInsured || selectedSumInsured

    return (
      <div className='card'>
        <div className='card-body'>
          <Row>
            <Col md={8}>
              <div className='d-flex justify-content-between align-items-center mb-4'>
                <h3 className='mb-0'>Enrolled Parents Details</h3>
                {enrolledSumInsured > 0 && (
                  <div className='badge badge-light-primary fs-6 p-3'>
                    Sum Insured: ₹{enrolledSumInsured.toLocaleString('en-IN')} (Parents share)
                  </div>
                )}
              </div>
              <div className='table-responsive mb-5'>
                <Table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bolder text-muted'>
                      <th>Name</th>
                      <th>Relation</th>
                      <th>Gender</th>
                      <th>Date of Birth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {membersToDisplay.map((member) => (
                      <tr key={member.Id}>
                        <td>
                          <div className='d-flex align-items-center'>
                            <div className='d-flex justify-content-start flex-column'>
                              <div className='text-dark fw-bolder fs-6'>{member.Name}</div>
                            </div>
                          </div>
                        </td>
                        <td>{member.Relation === 'ParentInLaw' ? 'Parent-in-law' : member.Relation}</td>
                        <td>{member.Gender}</td>
                        <td>{formatDateIST(member.Dob)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Col>
            <Col md={4}>
              <div className='card bg-light p-4 sticky-top' style={{ top: '20px' }}>
                <h4 className='mb-4'>Premium Summary</h4>

                {frozenPremium.dependentPremium > 0 && (
                  <div className='mb-3'>
                    <strong>Parents Premium:</strong>
                    <div className='float-end'>
                      ₹{frozenPremium.dependentPremium.toLocaleString('en-IN')}
                    </div>
                  </div>
                )}

                {frozenPremium.total > 0 ? (
                  <>
                    <hr />
                    <div className='mb-2'>
                      <span>Total Amount:</span>
                      <span className='float-end'>
                        ₹{frozenPremium.dependentPremium.toLocaleString('en-IN')}
                      </span>
                    </div>
                    {gstRate > 0 && (
                      <div className='mb-2'>
                        <span>GST ({(gstRate * 100).toFixed(0)}%):</span>
                        <span className='float-end'>₹{frozenPremium.gst.toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    <hr />
                    <h5 className='text-primary'>
                      Total Payable:
                      <span className='float-end'>₹{frozenPremium.total.toLocaleString('en-IN')}</span>
                    </h5>
                    <div className='text-muted fs-7 mt-2 text-end'>
                      The total premium will be deducted from your next salary.
                    </div>
                  </>
                ) : (
                  <div className='text-muted text-center py-5'>
                    No additional premium applicable.
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </div>
    )
  }

  // ─── Success State ──────────────────────────────────────────────────
  if (showSuccess) {
    return (
      <div className='card'>
        <div className='card-body text-center py-5'>
          <div className='mb-4'>
            <KTSVG path='/media/icons/duotune/general/gen043.svg' className='svg-icon-3x svg-icon-success' />
          </div>
          <h2 className='text-success mb-4'>Thank You!</h2>

          {premiumInfo.total > 0 ? (
            <div className='alert alert-info d-inline-block p-4'>
              <span className='fs-4'>
                An amount of <strong>₹{premiumInfo.total.toLocaleString('en-IN')}</strong> will be
                deducted from your salary.
              </span>
            </div>
          ) : (
            <div className='fs-4 text-gray-600'>Your enrollment is complete.</div>
          )}
        </div>
      </div>
    )
  }

  // ─── Active Enrollment State ────────────────────────────────────────
  return (
    <div className='card'>
      <div className='card-body'>
        {error && !showAddModal && (
          <div className='mb-4 text-danger fw-bold border border-danger p-3 rounded'>{error}</div>
        )}
        <Row>
          <Col md={8}>
            <div className='d-flex justify-content-between align-items-center mb-4'>
              <div>
                <h3 className='mb-0'>Parental Policy - Family Details</h3>
                <div className='text-muted fs-7 mt-1'>
                  Coverage: Up to 2 Parents + 2 Parent-in-laws (max 4 total). Floater sum insured.
                </div>
              </div>
              <Button
                variant={isMaxParentsReached ? 'secondary' : 'primary'}
                onClick={handleAddClick}
                disabled={isMaxParentsReached}
                title={isMaxParentsReached ? `Maximum reached (2 Parents + 2 Parent-in-laws)` : 'Add a parent or parent-in-law'}
              >
                {isMaxParentsReached ? 'Max Parents Added' : 'Add Parent'}
              </Button>
            </div>

            <div className='table-responsive mb-5'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bolder'>
                    <th className='min-w-150px'>Name</th>
                    <th className='min-w-100px'>Relation</th>
                    <th className='min-w-100px'>Gender</th>
                    <th className='min-w-100px'>DOB</th>
                    <th className='min-w-100px'>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dependents.map((dep) => (
                    <tr key={dep.id} className='text-capitalize text-dark'>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='d-flex justify-content-start flex-column'>
                            <span className='fw-bolder text-hover-primary fs-6 text-dark'>
                              {dep.name}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {dep.relation === 'ParentInLaw' ? 'Parent-in-law' : dep.relation}
                        </span>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {dep.gender}
                        </span>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {formatDateIST(dep.dob)}
                        </span>
                      </td>
                      <td>
                        {dep.relation !== 'Self' && (
                          <div className='d-flex gap-2'>
                            <Button
                              variant='primary'
                              size='sm'
                              onClick={() => handleEditClick(dep)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant='danger'
                              size='sm'
                              onClick={() => removeDependent(dep.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Sum Insured Selection */}
            <div className='mb-5'>
              <h5 className='mb-3'>Select Sum Insured</h5>
              <div className='d-flex gap-3 flex-wrap'>
                {SUM_INSURED_OPTIONS.map((opt) => {
                  const entry = PREMIUM_TABLE[opt.value]
                  return (
                    <div
                      key={opt.value}
                      className={`border rounded p-3 flex-fill text-center cursor-pointer ${
                        selectedSumInsured === opt.value
                          ? 'border-primary bg-light-primary'
                          : 'border-gray-300 bg-white'
                      }`}
                      style={{ cursor: 'pointer', minWidth: '180px' }}
                      onClick={() => setSelectedSumInsured(opt.value)}
                    >
                      <Form.Check
                        type='radio'
                        label={
                          <span className='fw-bold fs-6'>
                            {opt.label} (₹{opt.value.toLocaleString('en-IN')})
                          </span>
                        }
                        name='sumInsured'
                        id={`si-${opt.value}`}
                        checked={selectedSumInsured === opt.value}
                        onChange={() => setSelectedSumInsured(opt.value)}
                      />
                      {entry && (
                        <div className='text-muted fs-8 mt-1'>
                          1P: ₹{entry.p1.toLocaleString('en-IN')} | 2P: ₹{entry.p2.toLocaleString('en-IN')}
                          <br />
                          3P: ₹{entry.p3.toLocaleString('en-IN')} | 4P: ₹{entry.p4.toLocaleString('en-IN')}
                          <br /><small>(Excl. GST@{(gstRate * 100).toFixed(0)}%)</small>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Premium Rate Card */}
            {parentCount > 0 && (
              <div className='mb-5'>
                <h5 className='mb-3'>Group Premium Rate Card</h5>
                <div className='table-responsive'>
                  <table className='table table-bordered table-sm align-middle text-center'>
                    <thead>
                      <tr className='bg-primary text-white'>
                        <th className='text-start ps-3'>Sum Insured</th>
                        <th>1 Parent</th>
                        <th>2 Parents</th>
                        <th>3 Parents</th>
                        <th>4 Parents</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SUM_INSURED_OPTIONS.map((opt) => {
                        const entry = PREMIUM_TABLE[opt.value]
                        const isSelected = selectedSumInsured === opt.value
                        return (
                          <tr key={opt.value} className={isSelected ? 'bg-light-primary fw-bold' : ''}>
                            <td className='text-start ps-3 fw-bold'>₹{opt.value.toLocaleString('en-IN')}</td>
                            <td className={parentCount === 1 && isSelected ? 'bg-success text-white' : ''}>
                              ₹{entry.p1.toLocaleString('en-IN')}
                            </td>
                            <td className={parentCount === 2 && isSelected ? 'bg-success text-white' : ''}>
                              ₹{entry.p2.toLocaleString('en-IN')}
                            </td>
                            <td className={parentCount === 3 && isSelected ? 'bg-success text-white' : ''}>
                              ₹{entry.p3.toLocaleString('en-IN')}
                            </td>
                            <td className={parentCount === 4 && isSelected ? 'bg-success text-white' : ''}>
                              ₹{entry.p4.toLocaleString('en-IN')}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={5} className='text-muted fs-8 text-start ps-3'>
                          * All premiums are excluding GST@{(gstRate * 100).toFixed(0)}%. Group premium applicable.
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </Col>

          {/* ─── Premium Summary Sidebar ─────────────────────────── */}
          <Col md={4}>
            <div className='card bg-light p-4 sticky-top' style={{ top: '20px' }}>
              <h4 className='mb-4'>Premium Summary</h4>

              <div className='mb-3 p-3 bg-white rounded border'>
                <div className='text-muted fs-7'>Selected Sum Insured</div>
                <div className='fw-bolder fs-4 text-primary'>
                  ₹{selectedSumInsured.toLocaleString('en-IN')}
                </div>
                <div className='text-muted fs-8 mt-1'>Parents share this sum insured</div>
              </div>

              {premiumInfo.dependentCount > 0 && (
                <div className='mb-3 d-flex justify-content-between'>
                  <strong>Parents Premium ({getPremiumLabel(premiumInfo.dependentCount)}):</strong>
                  <span className='fw-bold'>₹{premiumInfo.dependentPremium.toLocaleString('en-IN')}</span>
                </div>
              )}

              {premiumInfo.dependentCount > 0 ? (
                <>
                  <hr />
                  <div className='mb-2 d-flex justify-content-between'>
                    <span>Base Premium:</span>
                    <span>₹{premiumInfo.dependentPremium.toLocaleString('en-IN')}</span>
                  </div>
                  {gstRate > 0 && (
                    <div className='mb-2 d-flex justify-content-between'>
                      <span>GST ({(gstRate * 100).toFixed(0)}%):</span>
                      <span>₹{premiumInfo.gst.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <hr />
                  <h5 className='text-primary d-flex justify-content-between'>
                    <span>Total Payable:</span>
                    <span>₹{premiumInfo.total.toLocaleString('en-IN')}</span>
                  </h5>
                  <div className='text-muted fs-7 mt-2 text-end'>
                    This amount will be deducted from your salary.
                  </div>
                </>
              ) : (
                <div className='text-muted text-center py-5'>
                  No additional premium applicable.
                  <br />
                  <small>Add parents to see premium.</small>
                </div>
              )}

              <div className='d-grid gap-2 mt-4'>
                <Button
                  variant='success'
                  size='lg'
                  onClick={() => setShowConfirmModal(true)}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className='spinner-border spinner-border-sm me-2'
                        role='status'
                        aria-hidden='true'
                      ></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Enrollment'
                  )}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Add Parent Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false)
          setEditingDependentId(null)
          setError(null)
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{editingDependentId ? 'Edit Parent' : 'Add Parent'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <div className='mb-4 text-danger fw-bold border border-danger p-3 rounded'>
              {error}
            </div>
          )}
          <div className='alert alert-light-info border border-info p-3 mb-4'>
            <div className='fs-8 text-gray-700'>
              <strong>Allowed Relations:</strong> Up to 2 Parents (Father/Mother) + Up to 2 Parent-in-laws (Father-in-law/Mother-in-law)
            </div>
          </div>
          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>Name</Form.Label>
              <Form.Control
                type='text'
                value={newDependent.name}
                onChange={(e) => setNewDependent({ ...newDependent, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Relation</Form.Label>
              <Form.Select
                value={newDependent.relation}
                onChange={(e) => setNewDependent({ ...newDependent, relation: e.target.value })}
              >
                <option value=''>Select Relation</option>
                {RELATIONS.map((r) => (
                  <option key={r} value={r}>
                    {r === 'ParentInLaw' ? 'Parent-in-law' : r}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Gender</Form.Label>
              <Form.Select
                value={newDependent.gender}
                onChange={(e) => setNewDependent({ ...newDependent, gender: e.target.value })}
              >
                <option value=''>Select Gender</option>
                {GENDERS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control
                type='date'
                value={newDependent.dob}
                onChange={(e) => setNewDependent({ ...newDependent, dob: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => { setShowAddModal(false); setEditingDependentId(null) }}>
            Cancel
          </Button>
          <Button variant='primary' onClick={saveDependent}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Final Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        backdrop='static'
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Enrollment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='alert alert-warning d-flex align-items-center'>
            <span className='svg-icon svg-icon-2hx svg-icon-warning me-4'>
              <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2x svg-icon-warning' />
            </span>
            <div className='d-flex flex-column'>
              {premiumInfo.total > 0 && (
                <>
                  <div className='fw-bold mb-1'>
                    Total Premium Deduction: ₹{premiumInfo.total.toLocaleString('en-IN')}
                  </div>
                  <div className='small'>This amount will be debited from your salary.</div>
                </>
              )}
              <div className='small mt-1'>
                <strong>
                  Please review and confirm your nominations carefully. Once submitted, no changes
                  can be made in the policy year.
                </strong>
              </div>
            </div>
          </div>
          <p className='mb-0 mt-3'>Do you want to proceed to enroll your parents?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowConfirmModal(false)}>
            Go Back
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              setShowConfirmModal(false)
              handleSubmit()
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                ></span>
                Submitting...
              </>
            ) : (
              'Yes, Enroll My Parents'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
